import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "antd";
import {
  ReloadOutlined,
  UserOutlined,
  BankOutlined,
  DollarOutlined,
  FundOutlined,
  SwapOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  RiseOutlined,
  FallOutlined,
  RightOutlined,
} from "@ant-design/icons";
import MainLayout from "../../components/layouts/MainLayout";
import { BOM } from "../../services/definedMethod";
import { connect } from "react-redux";
import "../../assets/styles/Dashboard.css";

// Chart colors mapped to semantic CSS variables so the SVGs stay
// in sync with the stylesheet tokens.  Resolved at render time via
// getComputedStyle, but we hardcode the *same* hex values here so
// SVG stroke works without runtime resolution.
const COLORS = {
  success: "#15CCAE",
  info: "#2563EB",
  purple: "#7C3AED",
  warning: "#D97706",
  teal: "#0D9488",
};

// ─── Donut SVG ──────────────────────────────────────────────────
const DonutChart = ({ data, size = 140, strokeWidth = 20, centerLabel }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const total = data.reduce((sum, d) => sum + d.value, 0);
  let offset = 0;

  return (
    <div className="db-donut-wrap">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#E2E8F0"
          strokeWidth={strokeWidth}
        />
        {data.map((seg, i) => {
          const len = (seg.value / total) * circumference;
          const dashOffset = -offset;
          offset += len;
          return (
            <circle
              key={seg.label}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={COLORS[seg.semantic]}
              strokeWidth={strokeWidth}
              strokeDasharray={`${len} ${circumference}`}
              strokeDashoffset={dashOffset}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
          );
        })}
      </svg>
      <div className="db-donut-center">
        <div
          className="db-donut-center-val"
          style={{ fontSize: size < 120 ? 16 : 22 }}
        >
          {total.toLocaleString()}
        </div>
        <div
          className="db-donut-center-lbl"
          style={{ fontSize: size < 120 ? 9 : 10 }}
        >
          {centerLabel || "Total"}
        </div>
      </div>
    </div>
  );
};

// ─── Static Data ────────────────────────────────────────────────

const kpiData = [
  {
    icon: <UserOutlined />,
    semantic: "info",
    value: "4,219",
    label: "Total customers",
    trend: "+5.2%",
    trendDir: "up",
    sub: (
      <>
        <span className="hl-success">218 new</span>&nbsp;this month
      </>
    ),
  },
  {
    icon: <BankOutlined />,
    semantic: "success",
    value: "42,940",
    label: "Active accounts",
    trend: "+2.1%",
    trendDir: "up",
    sub: (
      <>
        <span className="hl-success">30,000</span>&nbsp;savings · 6,500 current
      </>
    ),
  },
  {
    icon: <DollarOutlined />,
    semantic: "warning",
    value: "$284.7B",
    compact: true,
    label: "Total deposits",
    trend: "+8.4%",
    trendDir: "up",
    sub: "CASA + FD + RD",
  },
  {
    icon: <FundOutlined />,
    semantic: "purple",
    value: "$91.3B",
    compact: true,
    label: "Loan portfolio",
    trend: "−1.3%",
    trendDir: "down",
    sub: (
      <>
        <span className="hl-danger">2.4% NPA</span>&nbsp;ratio
      </>
    ),
  },
  {
    icon: <SwapOutlined />,
    semantic: "teal",
    value: "$14.8B",
    compact: true,
    label: "Treasury portfolio",
    trend: "+12%",
    trendDir: "up",
    sub: "T-bills + bonds · 24 contracts",
  },
];

const accountData = [
  { label: "Savings", value: 30000, semantic: "success", tab: "casa", route: "/acc0102" },
  { label: "Current", value: 6500, semantic: "info", tab: "casa", route: "/acc0101" },
  { label: "Loans", value: 2400, semantic: "purple", tab: "loans", route: "/acc0103" },
  { label: "Fixed Deposit", value: 2540, semantic: "warning", tab: "deposits", route: "/acc0106" },
  { label: "Recurring Dep.", value: 1500, semantic: "teal", tab: "deposits", route: "/acc0107" },
];

const customerData = [
  { label: "Active", value: 27, semantic: "success", pct: "38.6%" },
  { label: "Closed", value: 25, semantic: "info", pct: "35.7%" },
  { label: "Pending", value: 18, semantic: "warning", pct: "25.7%" },
];

const activityData = [
  {
    semantic: "success",
    icon: <CheckCircleOutlined />,
    title: "Savings deposit — James Mitchell",
    sub: "ACC-20049102 · Teller: R. Thompson",
    amount: "+$500,000",
    amountSemantic: "success",
    time: "09:14",
  },
  {
    semantic: "danger",
    icon: <WarningOutlined />,
    title: "Withdrawal — Sarah Henderson",
    sub: "ACC-20061845 · Current acct",
    amount: "−$250,000",
    amountSemantic: "danger",
    time: "09:02",
  },
  {
    semantic: "info",
    icon: <UserOutlined />,
    title: "New customer onboarded",
    sub: "David Clarke · ID: CUST-004219",
    amount: "Individual",
    amountSemantic: "muted",
    time: "08:55",
  },
  {
    semantic: "warning",
    icon: <BankOutlined />,
    title: "Fixed deposit activated",
    sub: "FD-ACC-018824 · 12 months",
    amount: "$2,000,000",
    amountSemantic: "warning",
    time: "08:41",
  },
  {
    semantic: "purple",
    icon: <FundOutlined />,
    title: "Loan disbursed — Robert Williams",
    sub: "LN-ACC-007234 · 12 months",
    amount: "$5,000,000",
    amountSemantic: "purple",
    time: "08:22",
  },
];

const pendingData = [
  { type: "New Account", ref: "ACC-042221", amount: "$50,000", priority: "High", pClass: "db-p-high", status: "Awaiting", sClass: "db-sp-pending" },
  { type: "Loan", ref: "LN-007411", amount: "$8,500,000", priority: "High", pClass: "db-p-high", status: "In review", sClass: "db-sp-review" },
  { type: "FD Activation", ref: "FD-018940", amount: "$3,000,000", priority: "Med", pClass: "db-p-med", status: "Awaiting", sClass: "db-sp-pending" },
  { type: "GL Posting", ref: "JE-002341", amount: "$12,000,000", priority: "Med", pClass: "db-p-med", status: "In review", sClass: "db-sp-review" },
  { type: "T-Bill", ref: "TRS-0048", amount: "$250,000,000", priority: "Low", pClass: "db-p-low", status: "Approved", sClass: "db-sp-active" },
];

const treasuryItems = [
  { type: "T-Bill", typeClass: "tbill", name: "FGN 91-Day Treasury Bill", tenor: "91d", rate: "18.75%", status: "Active" },
  { type: "Bond", typeClass: "bond", name: "FGN 10-Year Bond 2035", tenor: "10yr", rate: "17.50%", status: "Active" },
  { type: "T-Bill", typeClass: "tbill", name: "FGN 182-Day Treasury Bill", tenor: "182d", rate: "19.20%", status: "Active" },
  { type: "Bond", typeClass: "bond", name: "FGN 5-Year Bond 2030", tenor: "5yr", rate: "16.85%", status: "Pending" },
];

const sparkBars = [35, 42, 38, 55, 48, 62, 58, 75, 80, 72];

const TAB_LABELS = { all: "All Accounts", casa: "CASA", deposits: "Deposits", loans: "Loans" };

// ─── Dashboard ──────────────────────────────────────────────────
const Dashboard = ({ userData }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("all");
  const filteredAccounts = activeTab === "all"
    ? accountData
    : accountData.filter((a) => a.tab === activeTab);
  const accountTotal = filteredAccounts.reduce((s, d) => s + d.value, 0);

  return (
    <MainLayout>
      {/* ── COB Banner ── */}
      <div className="db-cob-strip db-fade-up">
        <div className="db-cob-dot" />
        <CheckCircleOutlined />
        <span>
          <strong>COB completed</strong> — All EOD batch jobs ran successfully.
          Business date advanced.
        </span>
        <span className="db-cob-right">3 batch jobs scheduled for today</span>
      </div>

      {/* ── Page Header ── */}
      <div className="db-page-header db-fade-up db-delay-1">
        <div>
          <h2 className="db-greeting">
            {BOM.Greet()}
            <span>{userData?.username}</span> 👋
          </h2>
          <div className="db-sub-greeting">
            Here's what's happening across your portfolio today.
          </div>
        </div>
      </div>

      {/* ── KPI Strip ── */}
      <div className="db-kpi-strip">
        {kpiData.map((kpi, idx) => (
          <div
            key={kpi.label}
            className={`db-kpi-card db-fade-up db-delay-${idx + 1}`}
          >
            <div className="db-kpi-top">
              <div className={`db-kpi-icon ${kpi.semantic}`}>{kpi.icon}</div>
              <div className={`db-kpi-trend ${kpi.trendDir}`}>
                {kpi.trendDir === "up" ? (
                  <RiseOutlined style={{ fontSize: 10 }} />
                ) : (
                  <FallOutlined style={{ fontSize: 10 }} />
                )}
                {kpi.trend}
              </div>
            </div>
            <div className={`db-kpi-value${kpi.compact ? " compact" : ""}`}>
              {kpi.value}
            </div>
            <div className="db-kpi-label">{kpi.label}</div>
            <div className="db-kpi-sub">{kpi.sub}</div>
          </div>
        ))}
      </div>

      {/* ── Row 2: Portfolio + Customers/Stats ── */}
      <div className="db-grid-2-1 db-fade-up db-delay-3">
        {/* Account Portfolio */}
        <div className="db-card">
          <div className="db-card-header">
            <div className="db-card-title">
              <BankOutlined />
              Account Portfolio
            </div>
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined />}
            />
          </div>
          <div className="db-card-tab-row">
            {Object.entries(TAB_LABELS).map(([key, label]) => (
              <button
                key={key}
                className={`db-card-tab${activeTab === key ? " active" : ""}`}
                onClick={() => setActiveTab(key)}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="db-portfolio-layout">
            <div className="db-portfolio-donut">
              <DonutChart data={filteredAccounts} size={140} strokeWidth={20} />
            </div>
            <div className="db-portfolio-bars">
              {filteredAccounts.map((acct) => (
                <div
                  key={acct.label}
                  className="db-acct-bar-row db-acct-bar-link"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(acct.route)}
                  onKeyDown={(e) => e.key === "Enter" && navigate(acct.route)}
                >
                  <div className="db-acct-bar-name">{acct.label}</div>
                  <div className="db-acct-bar-track">
                    <div
                      className={`db-acct-bar-fill ${acct.semantic}`}
                      style={{ width: `${(acct.value / accountTotal) * 100}%` }}
                    />
                  </div>
                  <div className="db-acct-bar-val">
                    {acct.value.toLocaleString()}
                  </div>
                  <RightOutlined className="db-acct-bar-arrow" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="db-col-stack">
          {/* Customers */}
          <div className="db-card" style={{ flex: 1 }}>
            <div className="db-card-header">
              <div className="db-card-title">
                <UserOutlined />
                Customers
              </div>
              <Button size="small" onClick={() => navigate("/cst0100")}>
                View all →
              </Button>
            </div>
            <div className="db-customers-body">
              <DonutChart data={customerData} size={100} strokeWidth={16} />
              <div className="db-legend">
                {customerData.map((c) => (
                  <div key={c.label} className="db-legend-item">
                    <div className={`db-legend-dot ${c.semantic}`} />
                    <span className="db-legend-lbl">{c.label}</span>
                    <span className="db-legend-val">{c.value}</span>
                    <span className="db-legend-pct">{c.pct}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Mini Stats */}
          <div className="db-card">
            <div className="db-mini-stats">
              <div className="db-mini-stat">
                <div className="db-ms-lbl">Pending approvals</div>
                <div className="db-ms-val warning">12</div>
                <div className="db-ms-sub">3 high priority</div>
              </div>
              <div className="db-mini-stat">
                <div className="db-ms-lbl">Today's transactions</div>
                <div className="db-ms-val">1,847</div>
                <div className="db-ms-sub">$3.24B volume</div>
              </div>
              <div className="db-mini-stat">
                <div className="db-ms-lbl">Overdue loans</div>
                <div className="db-ms-val danger">34</div>
                <div className="db-ms-sub">$842M at risk</div>
              </div>
              <div className="db-mini-stat">
                <div className="db-ms-lbl">NPA ratio</div>
                <div className="db-ms-val danger">2.4%</div>
                <div className="db-ms-sub">↑ 0.2% vs. prior</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Row 3: Activity + Pending + Treasury ── */}
      <div className="db-grid-3 db-fade-up db-delay-5">
        {/* Recent Activity */}
        <div className="db-card">
          <div className="db-card-header">
            <div className="db-card-title">
              <CheckCircleOutlined />
              Recent Activity
            </div>
          </div>
          <div className="db-feed-list">
            {activityData.map((item) => (
              <div key={item.title} className="db-feed-item">
                <div className={`db-feed-icon ${item.semantic}`}>
                  {item.icon}
                </div>
                <div className="db-feed-body">
                  <div className="db-feed-title">{item.title}</div>
                  <div className="db-feed-sub">{item.sub}</div>
                </div>
                <div className="db-feed-right">
                  <div className={`db-feed-amount ${item.amountSemantic}`}>
                    {item.amount}
                  </div>
                  <div className="db-feed-time">{item.time}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pending Approvals */}
        <div className="db-card">
          <div className="db-card-header">
            <div className="db-card-title">
              <ClockCircleOutlined />
              Pending Approvals
              <span className="db-count-badge">12</span>
            </div>
            <Button type="primary" size="small" className="of-primaryBtnColor">
              Review all
            </Button>
          </div>
          <div className="db-pending-wrap">
            <table className="db-pending-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Ref</th>
                  <th>Amount</th>
                  <th>Priority</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {pendingData.map((row) => (
                  <tr key={row.ref}>
                    <td>{row.type}</td>
                    <td className="ref-link">{row.ref}</td>
                    <td className="mono">{row.amount}</td>
                    <td>
                      <span className={`db-priority-dot ${row.pClass}`} />
                      {row.priority}
                    </td>
                    <td>
                      <span className={`db-status-pill ${row.sClass}`}>
                        {row.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Treasury Snapshot */}
        <div className="db-card">
          <div className="db-card-header">
            <div className="db-card-title">
              <SwapOutlined />
              Treasury Snapshot
            </div>
            <Button size="small">Full view →</Button>
          </div>
          <div className="db-card-body">
            <div className="db-treasury-header">
              <div>
                <div className="db-treasury-overline">Total portfolio value</div>
                <div className="db-treasury-value">$14.82B</div>
                <div className="db-treasury-gain">
                  <RiseOutlined style={{ fontSize: 11 }} />
                  +$342M MtM gain
                </div>
              </div>
              <div className="db-sparkline">
                {sparkBars.map((h, i) => (
                  <div
                    key={i}
                    className={`db-spark-bar${i >= 7 ? " high" : ""}`}
                    style={{ height: `${h}%` }}
                  />
                ))}
              </div>
            </div>

            <div className="db-trs-list">
              {treasuryItems.map((item) => (
                <div key={item.name} className="db-trs-row">
                  <div className={`db-trs-type ${item.typeClass}`}>
                    {item.type}
                  </div>
                  <div className="db-trs-name">{item.name}</div>
                  <div className="db-trs-tenor">{item.tenor}</div>
                  <div className="db-trs-rate">{item.rate}</div>
                  <div className="db-trs-status">
                    <span
                      className={`db-status-pill ${item.status === "Active" ? "db-sp-active" : "db-sp-pending"}`}
                    >
                      {item.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="db-trs-summary">
              <div className="db-trs-summary-item">
                <div className="db-trs-summary-label">T-Bills</div>
                <div className="db-trs-summary-val info">11</div>
              </div>
              <div className="db-trs-divider" />
              <div className="db-trs-summary-item">
                <div className="db-trs-summary-label">Bonds</div>
                <div className="db-trs-summary-val warning">13</div>
              </div>
              <div className="db-trs-divider" />
              <div className="db-trs-summary-item">
                <div className="db-trs-summary-label">Due ≤30d</div>
                <div className="db-trs-summary-val danger">3</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
});

export default connect(mapStateToProps)(Dashboard);
